import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Download, ExternalLink, FileText } from 'lucide-react'
import { db } from '../lib/db'
import GlassCard from '../components/ui/GlassCard'
import GradientButton from '../components/ui/GradientButton'
import PanelShell from '../components/ui/PanelShell'

interface GuideDoc {
  key: string
  title: string
  action: string
  description: string
}

// File names mirror api/_lib/clientGuideDocs.js, used only for the download
// filename — everything else (which docs exist, which this client can see)
// comes from the server's ?list=1 response below.
const FILE_NAMES: Record<string, string> = {
  welcomeGuide: 'fit-by-cassie-client-welcome-guide.pdf',
  intakeForm: 'fit-by-cassie-client-intake-form.pdf',
  hormonalHealthQuestionnaire: 'fit-by-cassie-womens-hormonal-health-questionnaire.pdf',
  waiver: 'fit-by-cassie-client-waiver.pdf',
  trainingGuide: 'fit-by-cassie-client-training-guide.pdf',
  competitionPrep: 'fit-by-cassie-competition-prep-final-4-weeks-peak-week-guide.pdf',
}

const ClientGuide: React.FC = () => {
  const navigate = useNavigate()
  const { user: instantUser, isLoading: authLoading } = db.useAuth()
  const token = instantUser?.refresh_token

  const [docs, setDocs] = useState<GuideDoc[]>([])
  const [docsLoading, setDocsLoading] = useState(true)
  const [selectedKey, setSelectedKey] = useState<string>('')
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')

  const selectedDoc = docs.find((d) => d.key === selectedKey) || docs[0]

  // Fetch which documents this signed-in client can actually see (the
  // Welcome/Intake/Questionnaire/Waiver always; Training Guide and
  // Competition Prep only once Cassie has flagged that client for them).
  useEffect(() => {
    if (authLoading) return
    if (!token) {
      setDocsLoading(false)
      return
    }

    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch('/api/client-guide?list=1', {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) throw new Error(`Request failed (${res.status})`)
        const body = await res.json()
        const available: GuideDoc[] = (body.docs || []).filter((d: any) => d.available)
        if (!cancelled) {
          setDocs(available)
          if (available.length) setSelectedKey(available[0].key)
        }
      } catch (err) {
        console.error('Failed to load document list:', err)
      } finally {
        if (!cancelled) setDocsLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [token, authLoading])

  useEffect(() => {
    if (authLoading) return
    if (!token) {
      setLoading(false)
      setErrorMessage('Please sign in to view your documents.')
      return
    }
    if (!selectedKey) {
      setLoading(false)
      return
    }

    let cancelled = false
    let objectUrl: string | null = null

    setLoading(true)
    setErrorMessage('')
    setBlobUrl(null)

    ;(async () => {
      try {
        const res = await fetch(`/api/client-guide?doc=${encodeURIComponent(selectedKey)}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body?.error || `Request failed (${res.status})`)
        }
        const blob = await res.blob()
        objectUrl = URL.createObjectURL(blob)
        if (!cancelled) setBlobUrl(objectUrl)
      } catch (err) {
        console.error('Failed to load document:', err)
        if (!cancelled) {
          setErrorMessage('This document isn’t available yet. Please check back soon.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [token, authLoading, selectedKey])

  return (
    <PanelShell
      eyebrow="My Resources"
      title="Your Documents"
      subtitle="Check-ins, nutrition guidelines, and the forms we'll work from together."
      actions={
        <GradientButton onClick={() => navigate('/dashboard')} variant="outline" size="sm" icon={ArrowLeft}>
          Back to dashboard
        </GradientButton>
      }
    >
      {docsLoading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold" />
        </div>
      ) : docs.length === 0 ? (
        <GlassCard hover={false}>
          <div className="text-center py-16">
            <FileText className="h-8 w-8 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No documents are available yet.</p>
          </div>
        </GlassCard>
      ) : (
      <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        {docs.map((doc) => {
          const isActive = doc.key === selectedKey
          return (
            <button
              key={doc.key}
              onClick={() => setSelectedKey(doc.key)}
              className={`text-left rounded-2xl border p-4 transition-colors ${
                isActive
                  ? 'border-gold bg-gold/5'
                  : 'border-gray-100 hover:border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start gap-3">
                <FileText className={`h-5 w-5 mt-0.5 ${isActive ? 'text-gold' : 'text-gray-400'}`} />
                <div>
                  <div className="font-semibold text-sm text-ink">{doc.title}</div>
                  <div className="text-xs font-bold uppercase tracking-wide text-gray-400 mt-0.5">
                    {doc.action}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{doc.description}</div>
                </div>
              </div>
            </button>
          )
        })}
      </div>

      <GlassCard hover={false}>
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gold" />
          </div>
        ) : errorMessage ? (
          <div className="text-center py-16">
            <FileText className="h-8 w-8 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">{errorMessage}</p>
          </div>
        ) : blobUrl ? (
          <>
            <div className="flex flex-wrap gap-3 mb-4">
              <GradientButton
                onClick={() => window.open(blobUrl, '_blank', 'noopener,noreferrer')}
                icon={ExternalLink}
                size="sm"
              >
                Open in new tab
              </GradientButton>
              <a href={blobUrl} download={FILE_NAMES[selectedDoc.key] || `${selectedDoc.key}.pdf`}>
                <GradientButton onClick={() => {}} icon={Download} variant="outline" size="sm">
                  Download
                </GradientButton>
              </a>
            </div>
            <div className="rounded-2xl overflow-hidden border border-gray-100" style={{ height: '75vh' }}>
              <iframe src={blobUrl} title={selectedDoc.title} className="w-full h-full border-0" />
            </div>
          </>
        ) : null}
      </GlassCard>
      </>
      )}
    </PanelShell>
  )
}

export default ClientGuide
